// The cart widget's list (SL1.0 `www/engine/get_cart.php:955-1143`, RULE-036). rbp answers every
// candidate for the entry (not bought this quarter or last, core for the store, active in the
// range), ordered by department name then code; the cart, the price and the cap are applied
// here, as SL1.0 applied them in the page loop (`:1024-1073`). Unlike SL1.0, a product the
// supplier marks unavailable at the active session depot is left out too (DD-S.2 axis 1, the one block on
// an add); an out-of-stock band alone is advisory and keeps the product. Pure.

import type { MissedProduct } from '../api/rbp-missed-products.client';
import type { MoneyValue } from '../shared/money';

/** At most this many products per department (`get_cart.php:1055-1056`). */
export const MISSED_PRODUCTS_PER_DEPARTMENT = 10;

export interface MissedProductGroup {
  readonly departmentName: string | null;
  readonly products: readonly MissedProduct[];
}

export interface MissedProductsInput {
  readonly candidates: readonly MissedProduct[];
  /** The SKUs in the cart. */
  readonly cartSkus: ReadonlySet<string>;
  /** The names of the cart's departments; null for an empty cart, which considers every one. */
  readonly cartDepartments: ReadonlySet<string> | null;
  /** Each candidate's unit price; absent or null when it has none. */
  readonly prices: ReadonlyMap<string, MoneyValue | null>;
  /** The SKUs the supplier marks unavailable at the active session depot; absent when none is known. */
  readonly unavailableSkus?: ReadonlySet<string>;
}

// Product codes compare up to case, as the server's citext columns compare them.
const codeKey = (code: string): string => code.toLowerCase();

/**
 * The candidates the widget shows, grouped by department in the server's order: never one in the
 * cart, only the cart's departments (every one for an empty cart), never one unavailable at the
 * depot, only a price above zero (a product priced 0 is skipped, `:1062-1066`), then at most ten
 * per department. The cap comes last, so it counts the products shown, never a dropped one (DF-19).
 */
export function narrowMissedProducts(input: MissedProductsInput): readonly MissedProductGroup[] {
  const unavailable = new Set([...(input.unavailableSkus ?? [])].map(codeKey));
  // Keyed by department, in the order a department first appears.
  const groups = new Map<string | null, MissedProduct[]>();
  for (const product of candidatesForCart(
    input.candidates,
    input.cartSkus,
    input.cartDepartments,
  )) {
    if (unavailable.has(codeKey(product.productCode))) continue;
    const price = input.prices.get(product.productCode);
    if (price === undefined || price === null || !(price.amount > 0)) continue;
    const products = groups.get(product.departmentName) ?? [];
    groups.set(product.departmentName, products);
    if (products.length < MISSED_PRODUCTS_PER_DEPARTMENT) products.push(product);
  }
  return [...groups].map(([departmentName, products]) => ({ departmentName, products }));
}

/**
 * The candidates the cart leaves eligible, before the price and the cap: never one in the cart, and
 * only the cart's departments (every one for an empty cart). These are the products to price.
 */
export function candidatesForCart(
  candidates: readonly MissedProduct[],
  cartSkus: ReadonlySet<string>,
  cartDepartments: ReadonlySet<string> | null,
): readonly MissedProduct[] {
  const inCart = new Set([...cartSkus].map(codeKey));
  return candidates.filter(
    (product) =>
      !inCart.has(codeKey(product.productCode)) &&
      (cartDepartments === null ||
        (product.departmentName !== null && cartDepartments.has(product.departmentName))),
  );
}

/**
 * The candidates still worth pricing, in the server's order: per department, until the products
 * known to be priced above zero, plus those not answered yet, reach the cap. `priceOf` answers a
 * known price (null for one with no price, or a price of zero), `'failed'` when the read itself
 * failed, or undefined while unknown. So a cart open prices about ten products per department, and
 * asks for the next ones only as earlier ones drop. A failed read stops the walk: it is not "no
 * price", so it gives no place back, and asking for the next products would only repeat it.
 */
export function skusToPrice(
  candidates: readonly MissedProduct[],
  priceOf: (sku: string) => MoneyValue | null | 'failed' | undefined,
): readonly string[] {
  const toPrice: string[] = [];
  const counted = new Map<string | null, number>();
  for (const product of candidates) {
    const taken = counted.get(product.departmentName) ?? 0;
    if (taken >= MISSED_PRODUCTS_PER_DEPARTMENT) continue;
    const price = priceOf(product.productCode);
    if (price === 'failed') return toPrice;
    if (price === undefined) {
      toPrice.push(product.productCode);
      counted.set(product.departmentName, taken + 1);
    } else if (price !== null && price.amount > 0) {
      counted.set(product.departmentName, taken + 1);
    }
  }
  return toPrice;
}
